import { Menu, Transition } from "@headlessui/react";
import {
  EllipsisHorizontalCircleIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import ConfirmBoxDelete from "components/ConfirmBoxDelete";
import { mainSelectedTab } from "modules/CarrierAgreementMgmt/atoms";
import {
  applianceEditState,
  applianceOfficeId,
  applianceShowDetails,
  applianceViewDetails,
  officeApplianceForEdit,
  showApplianceFromOffice,
} from "modules/CarrierAgreementMgmt/OfficeAppliance/atom";
import { useDeactivateOfficeAppliance } from "modules/CarrierAgreementMgmt/OfficeAppliance/hooks/useDeactivateOfficeAppliance";
import { useGetOfficeApplianceByOfficeId } from "modules/CarrierAgreementMgmt/OfficeAppliance/hooks/useGetOfficeApplianceByOfficeId";
import { permissionsSelector } from "modules/Login/selectors/permissionSelector";
import { OfficeTabs } from "modules/OfficeTab/OfficeTabs";
import { PERMISSIONS } from "modules/PermissionsConstants";
import { Fragment, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";

export const OfficeApplianceSideSection = ({ data }) => {
  //#region Data
  const { data: officeApplianceData } = useGetOfficeApplianceByOfficeId(
    data.officeId
  );
  const deactivateOfficeAppliance = useDeactivateOfficeAppliance();

  //#region States
  const [, setIsEditState] = useRecoilState(applianceEditState);
  const [, setShowApplianceDetails] = useRecoilState(applianceShowDetails);
  const [, setRowViewDetails] = useRecoilState(applianceViewDetails);
  const [, setRowForEdit] = useRecoilState(officeApplianceForEdit);
  const [, setApplianceOfficeId] = useRecoilState(applianceOfficeId);
  const [officeApplianceDetails, setOfficeApplianceDetails] = useState<any[]>(
    []
  );
  const [, setShowApplianceFromOffice] = useRecoilState(
    showApplianceFromOffice
  );
  const [, setSelectedIndex] = useRecoilState(mainSelectedTab);
  const [showModalToDelete, setShowModalToDelete] = useState(false);
  const [officeApplianceIdToDelete, setOfficeApplianceIdToDelete] =
    useState<any>([]);
  //#endregion

  //#region History and Permission
  const userPermissions = useRecoilValue(permissionsSelector);
  const editPermission = PERMISSIONS.permission_officeappliance_edit;
  const deletePermission = PERMISSIONS.permission_officeappliance_delete;
  const history = useHistory();
  //#endregion

  //#region Appliance Data UseEffect
  useEffect(() => {
    if (officeApplianceData) {
      setOfficeApplianceDetails(officeApplianceData);
    }
  }, [officeApplianceData]);
  //#endregion

  //#region Handle Link
  const handleLink = (moduleName) => {
    const url = `/officesettings/${moduleName}`;
    history.push(moduleName);
    const userTabs: any = [];

    for (const tab of OfficeTabs) {
      if (userPermissions.includes(tab.permission)) {
        userTabs.push({ to: tab.to });
      }
    }

    const index = userTabs.findIndex((t) => t.to === url);
    setSelectedIndex(index);
  };
  //#endregion

  //#region onClick Office Appliance
  const onClickOfficeAppliance = (rowData) => {
    handleLink("officeappliance");
    setRowViewDetails(rowData);
    setShowApplianceDetails(true);
    setShowApplianceFromOffice(true);
  };
  //#endregion

  //#region Handle Edit
  const handleEdit = (rowDetails) => {
    setRowForEdit({
      ...rowDetails,
    });
    setIsEditState(true);
    setShowApplianceFromOffice(true);
    handleLink("officeappliance");
  };
  //#endregion

  //#region View all Button function
  const onClickViewAll = () => {
    handleLink("officeappliance");
    setApplianceOfficeId(data.officeId);
  };
  //#endregion

  //#region Deactivate
  const handleSelectedData = (appliance) => {
    setOfficeApplianceIdToDelete([appliance.officeApplianceId]);
    setShowModalToDelete(true);
  };

  const handleDeactivate = () => {
    deactivateOfficeAppliance(officeApplianceIdToDelete);
    setShowModalToDelete(false);
  };
  //#endregion

  return (
    <>
      {officeApplianceDetails?.length > 0 ? (
        <>
          {officeApplianceDetails.map((appliance) => {
            return (
              <div key={appliance.officeApplianceId} className="">
                <div className="grid grid-cols-1 sm:grid-cols-1">
                  <div className="p-4 ">
                    <div className="flex justify-between m-1 gap-2">
                      <span className="pr-2">
                        <div
                          className="blueAndBold underline expandedData fontExpand text-xs cursor-pointer"
                          onClick={() => onClickOfficeAppliance(appliance)}
                        >
                          {appliance.officeApplianceId
                            ? "A-" +
                              String(appliance.officeApplianceId).padStart(
                                4,
                                "0"
                              )
                            : ""}
                        </div>
                      </span>
                      {userPermissions.includes(editPermission) && (
                        <Menu as="div" className="items-center flex">
                          <div>
                            <Menu.Button className="">
                              <EllipsisHorizontalCircleIcon
                                className="-mr-2 mt-1 h-5 w-5 text-primary bg-transparent rounded-full hover:bg-primary hover:text-white"
                                aria-hidden="true"
                              />
                            </Menu.Button>
                          </div>
                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <Menu.Items className="absolute right-0 mt-2 w-18 origin-top-right divide-y divide-gray-100 rounded-md bg-white box-shadow ring-1 ring-black ring-opacity-5 focus:outline-none transform opacity-100 scale-100">
                              <div className="px-1 py-1">
                                <Menu.Item>
                                  {({ active }) => (
                                    <button
                                      onClick={() => handleEdit(appliance)}
                                      disabled={false}
                                    >
                                      <label className="cursor-pointer fontLabel text-quaternary flex items-center place-content-center hover:text-primary underline   relative  px-2 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-quaternary focus:ring-opacity-70">
                                        <PencilSquareIcon className="h-4 w-4 " />{" "}
                                        Edit
                                      </label>
                                    </button>
                                  )}
                                </Menu.Item>
                              </div>
                              {userPermissions.includes(deletePermission) && (
                                <div className="px-1 py-1">
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        className={
                                          !appliance.isActive ||
                                          appliance.status === "Inactive"
                                            ? "opacity-50"
                                            : "opacity-100"
                                        }
                                        onClick={() => {
                                          handleSelectedData(appliance);
                                        }}
                                        disabled={
                                          !appliance.isActive ||
                                          appliance.status === "Inactive"
                                        }
                                      >
                                        <label className="cursor-pointer fontLabel text-secondary flex items-center place-content-center hover:text-hsecondary underline relative  px-2 text-xs font-medium">
                                          {" "}
                                          <TrashIcon className="h-4 w-4 " />{" "}
                                          Deactivate
                                        </label>
                                      </button>
                                    )}
                                  </Menu.Item>
                                </div>
                              )}
                            </Menu.Items>
                          </Transition>
                        </Menu>
                      )}
                    </div>
                    <div className="grid grid-cols-4 ">
                      <label className="col-span-2 mx-4 expandedLabel fontExpand">
                        Type:
                      </label>
                      <div className="col-span-2 expandedData fontExpand">
                        <span className="pr-2">
                          {appliance.officeApplianceType}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 ">
                      <label className="col-span-2 mx-4 expandedLabel fontExpand">
                        Brand:
                      </label>
                      <div className="col-span-2 expandedData fontExpand">
                        <span className="pr-2">
                          {appliance.officeApplianceBrand}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 ">
                      <label className="col-span-2 mx-4 expandedLabel fontExpand">
                        Purchase Date:
                      </label>
                      <div className="col-span-2 expandedData fontExpand">
                        <span className="pr-2">
                          {appliance.purchaseDate
                            ? new Date(
                                appliance.purchaseDate
                              ).toLocaleDateString()
                            : ""}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 ">
                      <label className="col-span-2 mx-4 expandedLabel fontExpand">
                        Purchase Order:
                      </label>
                      <div className="col-span-2 expandedData fontExpand">
                        <span className="pr-2">{appliance.purchaseOrder}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 ">
                      <label className="col-span-2 mx-4 expandedLabel fontExpand">
                        Item Price:
                      </label>
                      <div className="col-span-2 expandedData fontExpand">
                        <span className="pr-2">{`$${appliance.itemPrice}`}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {officeApplianceDetails.length > 2 && (
            <div className="bg-gray-50 p-2 flex justify-center">
              <button
                onClick={() => onClickViewAll()}
                className="text-primary underline font-semibold "
              >
                View All
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="flex justify-center text-xs italic col-span-3 p-2">
          No Office Appliance Found
        </div>
      )}
      {
        <ConfirmBoxDelete
          ModalTitle="Deactivate Office Appliance"
          qConfirmation={
            "Are you sure you want to deactivate this Office Appliance?"
          }
          isOpen={showModalToDelete}
          onClose={() => setShowModalToDelete(false)}
          onClickFunction={handleDeactivate}
          cancelButtonRef={undefined}
        />
      }
    </>
  );
};
